import { useCallback, useRef } from 'react'

import { NotesTextEditor } from '@factorialco/f0-react/dist/experimental'

import { useImageUploadForEditor } from 'modules/core/hooks/useImageUploadForEditor'
import i18n from 'modules/core/lib/i18n'
import { AttachedFile } from 'modules/trainings/lib/content/AttachedFile'

import { containsUploadingImage, extractImageUrls } from './editorContent'

const RESOURCE_TYPE = 'Trainings::Training'

export type InlineEditableContentProps = {
  value: string
  embeddedImages: AttachedFile[]
  readOnly: boolean
  onChange: (value: string) => void
  onEmbeddedImagesChange: (embeddedImages: AttachedFile[]) => void
  title: string
  onTitleChange: (value: string) => void
  trainingId: string
}

export const InlineEditableContent = ({
  value,
  embeddedImages,
  readOnly,
  onChange,
  onEmbeddedImagesChange,
  title,
  onTitleChange,
  trainingId,
}: InlineEditableContentProps) => {
  const lastTitleRef = useRef(title)
  const lastValueRef = useRef(value)

  const { onUpload } = useImageUploadForEditor({
    resourceType: RESOURCE_TYPE,
    resourceId: trainingId,
  })

  const parseDescriptionContent = (description: string) => {
    if (!description) return ''
    try {
      return JSON.parse(description)
    } catch {
      return description
    }
  }

  const handleTitleChange = (newTitle: string) => {
    if (newTitle !== lastTitleRef.current) {
      lastTitleRef.current = newTitle
      onTitleChange(newTitle)
    }
  }

  const handleContentChange = (json: unknown) => {
    if (containsUploadingImage(json)) return

    const imageUrls = extractImageUrls(json)
    const nextEmbeddedImages = embeddedImages.filter(
      (image) => image.url && imageUrls.has(image.url)
    )

    if (!sameEmbeddedImages(embeddedImages, nextEmbeddedImages)) {
      onEmbeddedImagesChange(nextEmbeddedImages)
    }

    const newValue = JSON.stringify(json)
    if (newValue !== lastValueRef.current) {
      lastValueRef.current = newValue
      onChange(newValue)
    }
  }

  const handleImageUpload = useCallback(
    async (file: File) => {
      const result = await onUpload(file)

      onEmbeddedImagesChange([
        ...embeddedImages.filter((image) => image.url !== result.url),
        new AttachedFile({
          url: result.url,
          signedId: result.signedId,
          filename: file.name,
        }),
      ])

      return result
    },
    [embeddedImages, onEmbeddedImagesChange, onUpload]
  )

  return (
    <NotesTextEditor
      initialEditorState={{
        content: parseDescriptionContent(value),
        title: title,
      }}
      onTitleChange={readOnly ? undefined : handleTitleChange}
      placeholder={i18n.t('trainings.content.block.page.description_placeholder')}
      onChange={({ json }) => handleContentChange(json)}
      readonly={readOnly}
      showBubbleMenu={!readOnly}
      imageUploadConfig={{ onUpload: handleImageUpload }}
      titlePlaceholder={i18n.t('trainings.content.block.page.title_placeholder')}
    />
  )
}

export const sameEmbeddedImages = (left: AttachedFile[], right: AttachedFile[]): boolean => {
  if (left.length !== right.length) return false

  return left.every((file, index) => {
    const other = right[index]

    return (
      file.url === other?.url &&
      file.signedId === other?.signedId &&
      file.filename === other?.filename
    )
  })
}
